import { PopupService } from './popup.service';
import { AwsFieldsModel } from './../models/awsFields.model';
import { ApiService } from './api.service';
import { EventEmitter, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import * as dayjs from 'dayjs';
import { BehaviorSubject, fromEvent, Observable, Subject, Subscription } from "rxjs";
import { debounceTime, map, takeUntil } from "rxjs/operators";
import { DirectMessage, MessageToSocket, MessageToUser, NotificationUserMessage, RoomMessage, User, UserMessage } from "../models";
import { BypassAPIGlobalHandleEnums, ChatFileScanStatus, MessageType, UserRole } from '../constants';
import { ToastService } from "./toast.service";
import { WebsocketService } from "./websocket.service";
import { UtilityService } from "./utility.service";
import { HttpParams } from '@angular/common/http';

@Injectable({
	providedIn: 'root'
})
export class ChatService {

	messages: Subject<MessageToUser | MessageToSocket>;
	notifications: NotificationUserMessage = {};
	rooms: RoomMessage[];
	roomActive: RoomMessage;
	chatMessages: DirectMessage[] = [];
	chatMessage: DirectMessage;
	nextRoomId: string;
	initialMessage = { "action": "notifications.user" };
	messageStack: MessageToUser[] = [];
	CHAT_URL: string = "/message/ws";
	isSocketInitialized: boolean = false;
	lastChatInitDate: dayjs.Dayjs;
	isMoreMessages: boolean = true;
	isLoadMoreMessagesAllowed: boolean = true;
	isNewChatOpened: boolean = true;
	isChatVisible: boolean = false;
	openedRoomId: string;

	private wsOpenSubscription: Subscription;
	private wsRestartSubscription: Subscription;

	private onRestart: boolean;
	private user: User
	private visibilitySubDestroy$ = new Subject<void>();

	onNewMessage = new EventEmitter();
	onUnreadMessages = new EventEmitter();
	onNewChatMessages = new EventEmitter();

	rooms$ = new BehaviorSubject<Array<RoomMessage>>([]);
	archivedRooms$ = new BehaviorSubject<Array<RoomMessage>>([]);
	onChatPartnerChange = new EventEmitter<UserMessage>();

	// OnReady's first value is null, true is when active and false when socket dies or closed
	onReady: BehaviorSubject<boolean> = new BehaviorSubject(null);

	private _chatPartner: UserMessage;
	private _roomsRequestStartTime: number | null = null;
	private _totalRoomsCount: number | null = null;

	private currentRoomsOffset: number = 0;
	private readonly ROOMS_LIMIT: number = 20;
	public hasMoreRooms: BehaviorSubject<boolean> = new BehaviorSubject(true);

	private archivedRooms: RoomMessage[] = [];
	private currentArchivedRoomsOffset: number = 0;
	private _totalArchivedRoomsCount: number | null = null;
	public hasMoreArchivedRooms: BehaviorSubject<boolean> = new BehaviorSubject(false);

	set chatPartner(value: UserMessage) {
		this._chatPartner = value;
		this.onChatPartnerChange.emit(value);
	}

	get chatPartner() {
		return this._chatPartner;
	}

	constructor(
		public wsService: WebsocketService,
		private toastService: ToastService,
		private translationService: TranslateService,
		private utilityService: UtilityService,
		private apiService: ApiService,
		private popupService: PopupService,
	) {
		if (this.utilityService.isBrowser()) {
			this.CHAT_URL = `wss://${location.host}/message/ws`;
		}
	}

	loadMoreChatRooms(): void {
		this.currentRoomsOffset += this.ROOMS_LIMIT;
		this.sendMessage({ "action": "rooms.get", "offset": this.currentRoomsOffset, "limit": this.ROOMS_LIMIT });
	}

	refreshRooms(): void {
		this.currentRoomsOffset = 0;
		this.sendMessage({ "action": "rooms.get", "offset": 0, "limit": this.ROOMS_LIMIT });
	}

	private mergeRoomsById(existing: RoomMessage[], incoming: RoomMessage[]): RoomMessage[] {
		const merged = [...existing];
		const indexById = new Map<string, number>(merged.map((room, index) => [room.id, index]));

		for (const room of incoming) {
			const existingIndex = indexById.get(room.id);
			if (existingIndex === undefined) {
				indexById.set(room.id, merged.length);
				merged.push(room);
			} else {
				merged[existingIndex] = room;
			}
		}

		return merged;
	}

	loadArchivedRooms(loadMore: boolean = false): void {
		if (loadMore) {
			this.currentArchivedRoomsOffset += this.ROOMS_LIMIT;
		} else {
			this.currentArchivedRoomsOffset = 0;
			this.archivedRooms = [];
		}
		this.sendMessage({ "action": "rooms.archived", "offset": this.currentArchivedRoomsOffset, "limit": this.ROOMS_LIMIT });
	}

	archiveRoom(roomId: string): void {
		this.apiService.post('message/room/archive', { room: roomId }).subscribe(() => {
			this.rooms = this.rooms?.filter(r => r.id !== roomId) ?? [];
			this.rooms$.next([...this.rooms]);
			this.loadArchivedRooms();
		});
	}

	unarchiveRoom(roomId: string): void {
		this.apiService.post('message/room/unarchive', { room: roomId }).subscribe(() => {
			this.archivedRooms = this.archivedRooms?.filter(r => r.id !== roomId) ?? [];
			this.archivedRooms$.next([...this.archivedRooms]);
			this.refreshRooms();
		});
	}

	subscribeMessages(): void {
		this.messages.subscribe((msg: MessageToUser) => {
			switch (msg.type) {
				case 'notifications.user':
					msg.data = new NotificationUserMessage(msg.data);
					this.notifications = msg.data;
					break;
				case 'rooms':
					const endTime = performance.now();
					if (this._roomsRequestStartTime) {
						const responseTime = endTime - this._roomsRequestStartTime;
						console.log(`Rooms.get response time: ${responseTime}ms`);
						this._roomsRequestStartTime = null;
					}
					const totalCountFromMsg = msg?.data?.page?.sum;
					if (typeof totalCountFromMsg === 'number') {
						this._totalRoomsCount = totalCountFromMsg;
					}

					const newRooms = msg.data.rooms as RoomMessage[];
					if (!this.rooms || this.currentRoomsOffset === 0) {
						this.rooms = newRooms;
					} else {
						this.rooms = this.mergeRoomsById(this.rooms, newRooms);
					}
					const loadedCount = this.rooms?.length || 0;
					this.hasMoreRooms.next(newRooms.length === this.ROOMS_LIMIT && loadedCount < (this._totalRoomsCount as number));

					if (this.rooms?.length) {
						this.translationService.get('ROUTES.Navigation_messages').subscribe(translation => {
							const isChatPageOpened = location.pathname.includes(translation);
							//The roomActive Reference needs to be updated each time when the rooms are updated from the socket
							if (isChatPageOpened) {
								if (this.roomActive) {
									this.roomActive = this.rooms.find(r => r.id == this.roomActive.id);
									this.setChatPartner();
									this.unReadMessages();
								} else {
									this.roomActive = this.rooms[0];
									this.setChatPartner();
								}
								if (this.onRestart) {
									this.getChatMessages();
								}
							}
							if (this.onRestart) {
								this.onRestart = false;
							}
							// Emit a fresh array reference for change detection in subscribers.
							this.rooms$.next([...(this.rooms ?? [])]);
							if (this.nextRoomId) {
								const pending = this.rooms.find(r => r.id == this.nextRoomId);
								if (pending) {
									this.openRoom(this.nextRoomId);
									this.nextRoomId = null;
								}
							}
						})
					}

					if (!this.onReady.value) {
						// When the rooms are loaded, the chat service full connection is ready.
						this.onReady.next(true);
					}
					break;
				case 'rooms.archived':
					const archivedTotal = msg?.data?.page?.sum;
					if (typeof archivedTotal === 'number') {
						this._totalArchivedRoomsCount = archivedTotal;
					}
					const newArchivedRooms = msg.data.rooms as RoomMessage[];
					if (this.currentArchivedRoomsOffset === 0) {
						this.archivedRooms = newArchivedRooms;
					} else {
						this.archivedRooms = [...this.archivedRooms, ...newArchivedRooms];
					}
					const archivedLoadedCount = this.archivedRooms?.length || 0;
					this.hasMoreArchivedRooms.next(archivedLoadedCount < (this._totalArchivedRoomsCount as number));
					this.archivedRooms$.next([...this.archivedRooms]);
					break;
				case 'messages':
					msg.data = msg.data as DirectMessage[];
					this.isMoreMessages = msg?.data?.length || false;
					this.isLoadMoreMessagesAllowed = true;

					if (this.isMoreMessages) {
						this.isNewChatOpened = !this.isSameRoom(msg.data);
						if (this.isNewChatOpened) {
							this.lastChatInitDate = dayjs();
							this.chatMessages = msg.data;
							this.markMessageRead(this.chatMessages[0].id);
							this.onNewChatMessages.emit();
						} else {
							this.chatMessages = this.chatMessages.concat(msg.data);
							this.markMessageRead(this.chatMessages[0].id)
						}
					}
					break;
				case 'message.edited':
					const editedIdx = this.chatMessages.findIndex(m => m.id === msg.data?.id);
					if (editedIdx !== -1) {
						this.chatMessages[editedIdx] = new DirectMessage({ ...this.chatMessages[editedIdx], ...msg.data });
					}
					this.onNewMessage.emit();
					break;
				case 'message.deleted':
					const deletedIdx = this.chatMessages.findIndex(m => m.id === msg.data?.id);
					if (deletedIdx !== -1) {
						this.chatMessages[deletedIdx] = new DirectMessage({ ...this.chatMessages[deletedIdx], ...msg.data });
					}
					this.onNewMessage.emit();
					break;
				case 'message':
					this.trackFirstMessageEvent();
					this.onNewMessage.emit();
					msg.data = new DirectMessage(msg.data);
					if (this.archivedRooms?.find(r => r.id === msg.data.room)) {
						this.archivedRooms = this.archivedRooms.filter(r => r.id !== msg.data.room);
						this.archivedRooms$.next([...this.archivedRooms]);
						this.refreshRooms();
					}
					if (!this.isChatVisible) {
						if (msg.data.user.Id != this.user.id) {
							this.refreshRooms();
							const toastMessage = msg.data.message && msg.data.message.includes('message.') ? 'texts.' + msg.data.message : msg.data.message;
							this.toastService.showMsg(toastMessage, MessageType.info,
								{
									title: msg.data.user.Forename ? msg.data.user.Forename : msg.data.user.forename,
									link: 'Navigation_messages/' + msg.data.room,
								});
						} else {
							this.markMessageRead(msg.data.id);
							this.refreshRooms();
						}
					} else if (this.roomActive?.id == msg.data.room) {
						const roomId = msg.data.room;
						const room = this.rooms?.find(r => r.id == roomId);
						if (room) {
							room.activity_at = msg.data.time;
							room.latest_message = { message: msg.data.message };
							this.rooms = [room, ...this.rooms.filter(r => r.id !== roomId)];
							this.rooms$.next([...this.rooms]);
						}
						this.chatMessage = msg.data;
						this.chatMessages = [this.chatMessage].concat(this.chatMessages);
						this.markMessageRead(this.chatMessage.id)
					} else if (msg.data.user_id == this.user.id) {
						this.markMessageRead(msg.data.id);
						this.refreshRooms();
					} else {
						this.refreshRooms();
					}
					break;
			}
		});
	}

	openRoom(roomID: string): void {
		if (!this.rooms || this.rooms.length === 0) {
			this.nextRoomId = roomID;
			return;
		}
		const roomActive = this.rooms.find(e => e.id == roomID)
			|| this.archivedRooms?.find(e => e.id == roomID);
		if (!roomActive) {
			this.nextRoomId = roomID;
			return;
		}
		if (roomActive.id != this.roomActive?.id) {
			this.roomActive = roomActive;
			this.chatMessages = [];
			this.unReadMessages();
			this.getChatMessages();
			this.setChatPartner();
		}
	}

	setChatPartner() {
		this.chatPartner = this.getRoomContact();
	}

	unReadMessages() {
		this.roomActive.unread_messages = 0;
		this.roomActive.unseen = false;
		this.onUnreadMessages.emit();
		this.rooms$.next([...(this.rooms ?? [])]);
	}

	markMessageRead(messageId: string) {
		this.messages.next({ action: "message.seen", message: messageId })
	}

	markRoomAsUnread(roomId: string): void {
		const room = this.rooms?.find(r => r.id === roomId);
		if (!room || room.unseen) return;
		this.apiService.post('message/chatroom/unseen', { roomId }).subscribe();
		room.unseen = true;
		this.rooms$.next([...this.rooms]);
	}

	getChatMessages(appendMessages = false): void {
		let before_message_id = '';
		if (appendMessages && this.chatMessages && this.chatMessages.length) {
			before_message_id = this.chatMessages[this.chatMessages.length - 1].id;
		}
		this.sendMessage({
			"action": "messages.get",
			"room": this.roomActive.id,
			"before_message": before_message_id,
			"limit": 8
		});
	}

	isSameRoom(data: DirectMessage[]): boolean {
		return this.chatMessages && this.chatMessages.length && this.chatMessages[0].room == data[0].room;
	}

	executeStack(): void {
		this.messageStack.forEach(message => {
			this.sendMessage(message);
		});
	}

	addStack(message: any) {
		this.messageStack.push(message);
	}

	sendMessage(message: any) {
		if (message.action === 'rooms.get') {
			this._roomsRequestStartTime = performance.now();
		}
		// Loading more messages while messages are already loaded is not allowed.
		if (message.action == 'messages.get') {
			if (!this.isLoadMoreMessagesAllowed) {
				return;
			}
			this.isLoadMoreMessagesAllowed = false;
		}
		this.messages.next(message);
	}

	sendInitialMessage() {
		this.sendMessage(this.initialMessage);
	}

	isReadyForScroll(): boolean {
		return this.lastChatInitDate && this.lastChatInitDate.isBefore(dayjs().subtract(1, 'second'));
	}

	getRoomContact(): UserMessage {
		return this.roomActive.users.find(user => user.id != this.user.id);
	}

	init(loggedInUser: User) {
		this.user = loggedInUser;
		this.start();
		let warningDialog;
		this.onReady.pipe(debounceTime(3000)).subscribe(ready => {
			if (ready === false) {
				if (this.isChatVisible) {
					warningDialog = this.popupService.openInfoDialog({ title: 'texts.Warning_Title', description: 'texts.Warning_Chat_Socket' })
				}
			} else if (warningDialog) {
				warningDialog.close();
			}
		})
	}

	reStart() {
		// If the page reloads and the user is on Chat page this function might be called twice, this check is needed just make sure to do not open 2 connection
		if (!this.onReady.value && !this.isSocketInitialized) {

			this.onRestart = true;
			this.start();
		}
	}

	close() {
		this.messages = null;
		this.isSocketInitialized = false;
		this.messageStack = [];
		this.wsOpenSubscription?.unsubscribe();
		this.wsRestartSubscription?.unsubscribe();
		this.chatMessages = [];
		this.roomActive = null;
		this.rooms = [];
		this.currentRoomsOffset = 0;
		this.hasMoreRooms.next(true);
		this.rooms$.next([]);
		this.archivedRooms = [];
		this.currentArchivedRoomsOffset = 0;
		this.archivedRooms$.next([]);
		this.hasMoreArchivedRooms.next(false);
		this.chatPartner = null;
		this.visibilitySubDestroy$.next();
	}

	private start() {
		this.wsOpenSubscription = this.wsService.onOpen.subscribe(() => {
			this.onSocketConnection();
		})

		this.connectWebSocket();

		this.subscribeMessages();

		this.addStack(this.initialMessage);

		this.addStack({ "action": "rooms.get", "offset": this.currentRoomsOffset, "limit": this.ROOMS_LIMIT });

		if (this.user?.role === UserRole.teacher || this.user?.role === UserRole.admin) {
			this.addStack({ "action": "rooms.archived", "offset": 0, "limit": this.ROOMS_LIMIT });
		}

		if (this.utilityService.isBrowser()) {
			const visibilitychange$ = fromEvent(document, 'visibilitychange');
			visibilitychange$.pipe(takeUntil(this.visibilitySubDestroy$)).subscribe(event => {
				if (!document.hidden && this.wsService.ws.readyState != 1) {
					if (this.onReady.value) {
						this.onReady.next(false);
					}
					this.close();
					this.reStart();
				}
			})
		}

		this.wsRestartSubscription = this.wsService.onErrorClose.subscribe(() => {
			// Websocket connection fails on Safari, while returning to the web app from the background
			// The operation couldn't be completed. (kNWErrorDomainPOSIX error 53 - Software caused connection abort)
			// Due to this issue, we close and reconnect
			if (this.onReady.value) {
				this.onReady.next(false);
			}
			this.close();
			this.reStart();
		})
	}

	private connectWebSocket(): void {
		// In case of the previous socket did not connect we are stopping it.
		if (!this.wsService.isSocketAlive && this.wsService.ws?.readyState === WebSocket.CONNECTING) {
			this.wsService.close();
		}

		this.messages = <Subject<MessageToUser>>this.wsService.connect(this.CHAT_URL).pipe(
			map(
				(response: MessageEvent): MessageToUser => {
					return response?.data ? JSON.parse(response.data) : {} as MessageToUser
				}
			)
		);
	}

	private onSocketConnection() {
		this.isSocketInitialized = true;
		this.executeStack();
	}

	/**
	 * Verifying and sending conversion events to Google Tag Manager
	 */
	trackFirstMessageEvent(): void {
		const isFirstResponseMessage = !this.chatMessages.find(e => e.user_id == this.user.id)
		const isNewChat = this.chatMessages.length < 15;
		if (isFirstResponseMessage && isNewChat) {
			const hasMessageHistory = this.chatMessages.length;
			const hasOnlySystemMessages = !this.chatMessages.find(e => !e.system)
			const isStudent = this.user.role == UserRole.student;
			if (hasMessageHistory && hasOnlySystemMessages) {
				this.utilityService.trackEvent('conversion', 'first_message', (isStudent ? 'student' : 'teacher') + '_request_chat', this.chatPartner?.id);
			} else {
				this.utilityService.trackEvent('conversion', 'first_message', (isStudent ? 'student' : 'teacher') + '_response', this.chatPartner?.id);
			}
		}
	}

	getUploadFieldsForFile(file: File, roomId: string): Observable<{ url: string, fields: AwsFieldsModel }> {
		// Errors are forwarded to the caller so a failed attachment can be restored
		// for retry; backend reason keys still surface as toasts.
		const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.ErrorHandle);
		return this.apiService.postWithHeaders('message/chat-file', {
			fileName: file.name,
			contentType: file.type || 'application/octet-stream',
			roomId
		}, headers);
	}

	/**
	 * Scan status (and, once clean, a presigned URL) for a chat file.
	 * `fileName` is the user-facing name: the backend puts it (and the matching
	 * content type) on the presigned URL, so "Save image as" and downloads get a
	 * proper name and extension instead of the S3 object key. With `download`
	 * the URL is served as an attachment instead of inline.
	 */
	getFileDownloadLink(fileKey: string, fileName: string, download = false): Observable<{ url?: string, scanStatus: ChatFileScanStatus, size?: number }> {
		let params = new HttpParams()
			.set('fileKey', fileKey)
			.set('fileName', fileName);
		if (download) {
			params = params.set('disposition', 'attachment');
		}
		// Opt out of global error handling so a 403 (file not accessible) is
		// forwarded to the caller instead of redirecting the user to the 403 page.
		const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
		return this.apiService.getWithHeaders('message/chat-file', headers, params);
	}

	getAndFormatMessageText(message: string): { message: string, fileKey: string } {
		let fileKey;
		if (message && message.includes('message.')) {
			message = 'texts.' + message;
		} else if (message && message.includes('fileKey') && message.includes('fileName')) {
			const data = JSON.parse(message);
			message = data.fileName;
			fileKey = data.fileKey;
		}
		return { message: message, fileKey: fileKey }
	}

}
